// Rafael Cordero Portfolio Data

export const personalInfo = {
  name: "Rafael T. Cordero",
  title: "Director of Information Technology & Facilities Services",
  tagline: "Strategic IT Leadership | Cybersecurity | K-8 Education Innovation",
  location: "Agoura Hills, CA",
  phone: "347-901-8522",
  email: "racorbas@gmail.com",
  linkedin: "https://www.linkedin.com/in/rafael-cordero/",
  photo: "https://customer-assets.emergentagent.com/job_a68dbf3b-ec2c-480e-bdd7-a05d016ea3f8/artifacts/he4yooav_Cordero%20%20IMG_3659%202.JPG",
  resumeUrl: "https://customer-assets-4nw71qhi.emergentagent.net/job_ed060bf4-78c4-4bef-8f1c-8bc498a56fe1/artifacts/26ea4ec49e48fbb0_Rafael%20Cordero%20-%20Resume%2010_26.pdf",
  coverLetterUrl: "https://customer-assets.emergentagent.com/job_a68dbf3b-ec2c-480e-bdd7-a05d016ea3f8/artifacts/468h3eza_CL_Milken%20Community%20School.pdf",
  languages: ["English", "Spanish"]
};

export const summary = `I am a senior technology and operations leader in independent K–8 education, currently serving as an administrative leader reporting directly to the Head of School. My work sits at the intersection of technology, instruction and institutional trust. This will ensure that digital systems, cybersecurity, and campus operations quietly and reliably support teaching, learning, and community life. I partner closely with my Head of School, CFO, Division Heads and Board to translate strategic priorities into secure, sustainable, and well-governed technology environments.

My leadership approach is service-oriented and collaborative, grounded in the belief that the best technology leadership in schools is thoughtful, calm, and mission-aligned. I am particularly interested in responsible innovation, including the careful integration of emerging technologies such as AI, where instructional value, privacy, and security must remain in balance. In addition to my leadership work, I have taught computer science and STEAM courses, which keeps my perspective rooted in the realities of classrooms and faculty needs.`;

export const experience = [
  {
    id: 1,
    company: "The Riviera Ridge School",
    location: "Santa Barbara, CA",
    role: "Director of Information Technology & Facilities Services",
    period: "June 2023 - Present",
    reportingTo: "Head of School",
    highlightCategories: [
      {
        title: "Building Infrastructure",
        items: [
          "Led a first-year, end-to-end rebuild of the school's IT infrastructure, replacing outdated systems with a modern, secure, and scalable environment.",
          "Designed and implemented a new network architecture, including upgraded switching, wireless access points, and network segmentation.",
          "Coordinated with Cox, Rapid Scale, OutsideOpen and the City of Santa Barbara to deploy high-speed fiber internet, resolving chronic connectivity issues and enabling reliable 1:1 computing.",
          "Retired legacy technologies and standardized platforms to improve system reliability, uptime, and user experience.",
          "Started and led an IT internship mentorship program in partnership with UCSB, supporting early-career technologists through hands-on experience and professional development."
        ]
      },
      {
        title: "Daily Operations",
        items: [
          "Serve as senior technology leader, partnering with the Head of School, CFO, and academic leadership to align technology with mission and instructional priorities.",
          "Execute a multi-year technology roadmap across infrastructure, cybersecurity, service delivery, and instructional support.",
          "Direct annual technology budgets ($100K OpEx / $300K CapEx) in alignment with institutional strategy.",
          "Implemented the FMX system, modernizing IT and facilities workflows through centralized ticketing and asset management.",
          "Provide executive oversight of SIS platforms (Blackbaud Education Management), ensuring secure access and accurate reporting.",
          "Lead cybersecurity strategy and incident response, including phishing and ransomware events aligned with FERPA, COPPA, and cyber-insurance requirements.",
          "Diagnosed a fleet-wide boot failure that rendered 10 of 90 newly deployed Apple silicon MacBooks unbootable, isolating the root cause — an MDM configuration profile disabling Apple Intelligence, whose cryptex removal triggered an incomplete LocalPolicy re-sign (Cryptex1 manifest hash and generation counter), leaving each Mac unable to authorize booting its own intact, sealed system volume — through controlled single-variable testing across hardware, macOS build, MDM tenant, enrollment profile and scoped payloads."
        ]
      },
      {
        title: "Facilities Management & Institutional Operations",
        items: [
          "Co-manage campus facilities and institutional operations with the Director of Campus Management, overseeing a three-person facilities team.",
          "Establish goals, weekly planning rhythms, and accountability structures to support campus safety and operational continuity.",
          "Align physical infrastructure, safety systems, and technology to support daily operations, emergency preparedness, and long-term capital planning."
        ]
      }
    ]
  },
  {
    id: 2,
    company: "Village School",
    location: "Pacific Palisades, CA",
    role: "Math/STEAM Specialist & IT Support Specialist",
    period: "August 2017 - June 2023",
    highlights: [
      "Improved learning outcomes for JK-3 students by creating math lab instruction and designing an AR sandbox to demonstrate behavioral differences.",
      "Led technology co-teach and implemented classroom tech including Minecraft and robotics at the Village School innovation lab."
    ]
  },
  {
    id: 3,
    company: "Freelance Technology Consultant",
    location: "",
    role: "Freelance Technology Consultant",
    period: "2012 - Present",
    highlights: [
      "Designed and built websites for organizations and independent professionals, delivering secure and maintainable solutions.",
      "Provided independent technical consulting on infrastructure, system selection, and security best practices."
    ]
  },
  {
    id: 4,
    company: "Select Inversiones C.A. / Bucros",
    location: "Caracas, Venezuela",
    role: "Project Manager (Telecommunications & IT Infrastructure)",
    period: "2009 - 2012",
    highlights: [
      "Delivered multi-site telecommunications and infrastructure projects from planning through cutover",
      "Managed scope, schedule, budget, vendors, and QA across concurrent deployments",
      "Negotiated vendor terms and managed contract performance"
    ]
  }
];

export const skills = {
  leadership: [
    "IT Operations Leadership",
    "Strategic Planning & Execution",
    "Technology Budgeting & Capital Planning",
    "Vendor & Contract Management",
    "Cross-Department Collaboration",
    "Team Development & Mentoring"
  ],
  technical: [
    "Cybersecurity Strategy & Incident Response",
    "Infrastructure & Cloud Operations",
    "Disaster Recovery & Business Continuity",
    "Data Governance & Privacy",
    "Responsible AI Integration",
    "1:1 Device Program Management"
  ],
  platforms: [
    "Google Workspace",
    "Apple 1:1 (Jamf)",
    "Blackbaud Education Management",
    "Unifi Networking",
    "Cloudflare",
    "VMware",
    "Cisco Networking",
    "Verkada Security Systems",
    "Asana"
  ]
};

export const education = [
  {
    degree: "M.S. Information Technology",
    focus: "Cybersecurity Focus",
    institution: "California Lutheran University",
    location: "Thousand Oaks, CA",
    year: "2023"
  },
  {
    degree: "B.S. Telecommunications Engineering",
    focus: "",
    institution: "Universidad Fermín Toro",
    location: "Barquisimeto, Venezuela",
    year: "2008"
  }
];

export const certifications = [
  {
    name: "Jamf Certified Associate",
    issuer: "June 2025",
    icon: "Apple"
  },
  {
    name: "ATLIS Leadership Program",
    issuer: "Dallas, July 2023 - Certification Program Completed",
    icon: "Award"
  },
  {
    name: "CALISBOA",
    issuer: "April 2025",
    icon: "Award"
  },
  {
    name: "CES Tech Conference",
    issuer: "Consumer Electronics Show, Las Vegas - January 2024 & January 2026",
    icon: "Award"
  },
  {
    name: "InTELA Group Planning Committee",
    issuer: "Independent School Technology Educators of Los Angeles",
    icon: "Award"
  }
];

export const mediaFeatures = [
  {
    id: 1,
    type: "article",
    title: "Combining Physical Security Solutions to Keep Schools Safe",
    publication: "EdTech Magazine",
    date: "Summer 2025",
    description: "Featured for implementing Verkada camera systems, panic buttons, and air quality sensors at The Riviera Ridge School to enhance campus safety.",
    quote: "The school has since installed three panic buttons on the campus that are wired into the phone and camera systems. If an emergency occurs, the system sends an alert, and the police are called.",
    url: "https://customer-assets.emergentagent.com/job_a68dbf3b-ec2c-480e-bdd7-a05d016ea3f8/artifacts/mhgm1887_EdTech%20magazine.pdf",
    icon: "Newspaper"
  },
  {
    id: 2,
    type: "podcast",
    title: "Server Room to Boiler Room: Unifying IT & Facilities",
    publication: "ATLIS Podcast",
    date: "August - 2025",
    description: "National podcast appearance discussing the integration of IT and facilities operations in independent schools.",
    url: "https://resources.theatlis.org/podcasts/server-room-boiler-room-unifying-it-facilities-rafael-cordero",
    icon: "Podcast"
  }
];

export const communityInvolvement = [
  {
    organization: "INTELA",
    role: "Planning Committee Member",
    description: "Independent School Technology Educators of Los Angeles"
  },
  {
    organization: "ATLIS",
    role: "Leadership Program Graduate",
    description: "Association of Technology Leaders in Independent Schools"
  }
];
